import {Component, ViewChild, ElementRef, AfterViewInit, OnDestroy} from '@angular/core';
import { SendMessageComponent } from '../message/message.component';
import { ChatMessageComponent } from '../chat-message/chat-message.component';
import {ChatService} from '../../services/chat.service';
import {AsyncPipe, NgFor, NgClass, CommonModule} from '@angular/common';
import { MessageType } from '../../enum/constants.enum';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { Observable, Subscription } from 'rxjs';
import { map } from 'rxjs/operators';
import { IMessageList } from '../../interfaces/types.interface';

@Component({
    selector: 'fyxt-chat',
    templateUrl: './chat.component.html',
    styleUrls: ['./chat.component.scss'],
    standalone: true,
    imports: [SendMessageComponent, ChatMessageComponent, AsyncPipe, NgFor, NgClass, CommonModule, MatProgressSpinnerModule]
})
export class ChatComponent implements AfterViewInit, OnDestroy {
  @ViewChild('messageContainer', { static: false }) messageContainer!: ElementRef;

  // Messages are stored newest-first; render them oldest-first so the newest
  // message is at the bottom (standard top-to-bottom chat layout).
  messagesOldestFirst$: Observable<IMessageList[]>;

  private scrollSubscription?: Subscription;
  private mutationObserver?: MutationObserver;
  private contentCheckScheduled = false;

  // If the user is within this distance of the bottom, keep following new
  // content (covers requirement 3: "almost at the very end").
  private readonly BOTTOM_THRESHOLD = 120;

  // Whether the user is at/near the bottom. While true, new content keeps the
  // view pinned to the bottom (requirements 1 & 3). While false, the user has
  // scrolled up to read and we leave their position untouched (requirement 2) —
  // in a normal top-to-bottom layout the browser preserves it natively.
  private atBottom = true;

  private onContainerScroll = () => {
    const el = this.messageContainer?.nativeElement;
    if (!el) return;
    this.atBottom = el.scrollHeight - el.scrollTop - el.clientHeight <= this.BOTTOM_THRESHOLD;
  };

  constructor(
    public chatService: ChatService
  ) {
    this.messagesOldestFirst$ = this.chatService.messages$.pipe(
      map((messages) => [...messages].reverse()),
    );
  }

  ngAfterViewInit() {
    // Explicit scroll request (e.g. after the user sends a message): jump to
    // the bottom and resume following.
    this.scrollSubscription = this.chatService.scrollToBottom$.subscribe(() => {
      this.atBottom = true;
      this.scrollToBottom();
    });

    const container = this.messageContainer?.nativeElement as HTMLElement | undefined;
    if (container) {
      container.addEventListener('scroll', this.onContainerScroll, { passive: true });

      // A single observer reacts to every content change: streamed tokens
      // (markdown re-render), new messages, and thinking-log expand/collapse.
      this.mutationObserver = new MutationObserver(() => this.scheduleContentCheck());
      this.mutationObserver.observe(container, {
        childList: true,
        subtree: true,
        characterData: true,
      });
    }
  }

  ngOnDestroy() {
    this.scrollSubscription?.unsubscribe();
    this.mutationObserver?.disconnect();
    const container = this.messageContainer?.nativeElement as HTMLElement | undefined;
    container?.removeEventListener('scroll', this.onContainerScroll);
  }

  trackMessage(index: number, message: { id?: string }): string | number {
    return message?.id || index;
  }

  // Coalesce bursts of mutations (many per streamed chunk) into one check per frame.
  private scheduleContentCheck() {
    if (this.contentCheckScheduled) return;
    this.contentCheckScheduled = true;
    requestAnimationFrame(() => {
      this.contentCheckScheduled = false;
      // Only follow new content if the user was already at the bottom. Otherwise
      // leave their scroll position alone — the browser keeps it stable since
      // new content is appended below the viewport.
      if (this.atBottom) {
        this.scrollToBottom();
      }
    });
  }

  private scrollToBottom() {
    const el = this.messageContainer?.nativeElement;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }

  protected readonly MessageType = MessageType
}
